import { SocketClient } from "cbcore-ts"
import { EXTEND, IF, nil, NO, UIColor, UIDialogView, UITextField, UITextView, UIView, YES } from "uicore-ts"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHCheckbox } from "../Custom components/RH/RHCheckbox"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHButtons, RHColors, RHDialogs } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { CBTableRowView, rowStateOf } from "../Custom components/CB/CBTableRowView"


interface CPVNode {
    id: string
    text: string
    children: CPVNode[]
    // Fields set internally by setTreeData and our selection logic
    _parent?: string
    _selected?: boolean
}

export type CPVSelectionExpansionMode =
    "none" |
    "selectDescendantsOnAdd"

export type CPVInviteCountProvider = (cpvCode: string, shouldLoad?: boolean) => number | undefined
export type CPVFoundCompanyCountProvider = (
    cpvCode: string,
    shouldLoad?: boolean
) => number | undefined

export interface CPVSummationRowInfo {
    amount: number
    isOverLimit: boolean
}

export type CPVSummationRowInfoProvider = (cpvCode: string, shouldLoad?: boolean) => CPVSummationRowInfo | undefined


export class CPVTreeDialogView extends UIDialogView {
    
    readonly contentView = new UIView()
        .configuredWithObject({
            backgroundColor: RHColor.whiteColor,
            style: {
                borderRadius: "12px",
                boxShadow: "0 8px 40px rgba(0,0,0,0.18)"
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Vali CPV-koodid",
            textColor: RHColors.textPrimary
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly instructionLabel = new UITextView()
        .configuredWithObject({
            text: "Sisesta märksõna otsimiseks. Klõpsa real valimiseks.",
            style: {
                fontSize: "0.85em",
                color: "#888888",
                fontStyle: "italic"
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly cpvTreeDataView = new RHDataView<CPVNode>()
        .configuredWithObject({
            isTreeView: YES,
            isSearchAvailable: YES,
            performsSearchFilteringSynchronously: YES,
            tableView: {
                allRowsHaveEqualHeight: NO
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly selectedItemBannerView = new UIView()
        .configuredWithObject({
            backgroundColor: RHDialogs.cpvTreeSelectedSummaryUnselectedBackgroundColor,
            style: {
                borderRadius: RHDialogs.cpvTreeSelectedSummaryBorderRadius,
                border: `1px solid ${RHDialogs.cpvTreeSelectedSummaryUnselectedBorderColor.stringValue}`,
                boxShadow: RHDialogs.cpvTreeSelectedSummaryUnselectedShadow
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly selectedItemLabel = new UITextView()
        .configuredWithObject({
            text: "Pole valitud",
            textColor: RHDialogs.cpvTreeSelectedSummaryUnselectedTextColor,
            style: {
                fontSize: RHDialogs.cpvTreeSelectedSummaryFontSize,
                fontWeight: "500",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
            }
        })
        .addedAsSubviewToView(this.selectedItemBannerView)
    
    readonly confirmButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Kinnita valik",
                style: {
                    fontSize: RHDialogs.cpvTreeFooterButtonFontSize
                }
            },
            variant: RHDialogs.cpvTreeConfirmButtonVariant,
            contentPadding: RHDialogs.cpvTreeFooterButtonContentPadding
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Tühista" }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly closeButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "✕" }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly bottomSpacerView = new UIView()
        .configuredWithObject({ userInteractionEnabled: NO })
        .addedAsSubviewToView(this)
    
    // State
    _cpvTreeData: CPVNode[] = []
    _selectedIds = new Set<string>()
    _cellDataMap = new WeakMap<RHCheckbox, CPVNode>()
    _onSelectionConfirmed?: (selectedCodes: string[]) => void
    _isDataLoaded = false
    _saveIsSuppressedOnDismiss = NO
    
    // ── Configurable presentation (defaults match the new-procurement picker) ──
    /** Dialog heading. */
    dialogTitle = "Vali CPV-koodid"
    /** Primary footer button label (confirm / search). */
    confirmButtonTitle = "Kinnita valik"
    /** Secondary footer button label (cancel / close). */
    cancelButtonTitle = "Tühista"
    /** YES = several codes at once (search); NO = a single code (new-procurement part). */
    allowsMultipleSelection = NO
    /** Interest-setting mode: selecting a parent returns that parent and all descendants exactly. */
    selectionExpansionMode: CPVSelectionExpansionMode = "none"
    /** Optional display-only suffix provider for invited-company counts. */
    inviteCountForCPVCode?: CPVInviteCountProvider
    /** Optional display-only suffix provider for companies found for the CPV code. */
    foundCompanyCountForCPVCode?: CPVFoundCompanyCountProvider
    /** Optional display-only provider for CPV summation row metadata. */
    summationInfoForCPVCode?: CPVSummationRowInfoProvider
    selectionDidChange?: (selectedCodes: string[]) => void
    /** YES = ancestors of selected rows show a partial checkbox state. */
    showsAncestorSelectionState = YES
    /** Codes that must remain selected and cannot be toggled off by the user. */
    lockedSelectedCodes: string[] = []
    /** YES keeps the legacy close/backdrop-as-confirm behavior. */
    confirmsSelectionOnDismiss = YES
    
    /**
     * Precomputed set of all node IDs that are ancestors of a currently selected node.
     * Rebuilt once on every selection change in O(depth) time, then checked in O(1)
     * per row in updateCell — eliminating the per-row recursive tree walk.
     */
    _ancestorIdsOfSelected = new Set<string>()
    
    /** Index into filteredData of the currently keyboard-highlighted row. */
    _highlightedRowIndex: number | undefined
    _highlightedNodeId: string | undefined
    _cachedSearchTextForWords = ""
    _cachedSearchWords: string[] = []
    _lastSearchTextForExpansionReset = ""
    /** True while updateCell is running — prevents programmatic selected= assignments
     *  from re-firing the PointerUpInside handler and toggling the selection back off. */
    _isUpdatingCells = false
    _onClose: (() => void) | undefined
    _hasScheduledMetadataLoadForVisibleRows = NO
    _hasScheduledFoundCompanyCountLoadForVisibleRows = NO
    _hasScheduledDisplayedRowsRefresh = NO
    
    
    constructor(elementID?: string) {
        super(elementID)
        
        this.view = this.contentView
        this.closeButton.configureWithObject({
            titleLabel: {
                text: RHButtons.dialogCloseTitle,
                style: {
                    fontSize: RHButtons.dialogCloseFontSize,
                    lineHeight: RHButtons.dialogCloseLineHeight
                }
            },
            variant: RHButtons.dialogCloseVariant,
            contentPadding: RHButtons.dialogCloseContentPadding,
            style: {
                borderRadius: RHButtons.dialogCloseBorderRadius
            }
        })
        this.cancelButton.configureWithObject({
            titleLabel: {
                style: {
                    fontSize: RHDialogs.cpvTreeFooterButtonFontSize
                }
            },
            variant: RHDialogs.cpvTreeCancelButtonVariant,
            contentPadding: RHDialogs.cpvTreeFooterButtonContentPadding
        })
        // Individual picker configurations decide whether tapping outside confirms.
        this.dismissesOnTapOutside = YES
        
        this._setupDescriptors()
        this._configureTreePresentation()
        this._setupButtonHandlers()
        this._setupKeyboardHandlers()
        
        // All keyboard navigation (arrows, enter, tab, escape) is implemented manually
        // above via the search field — see _setupKeyboardHandlers. The table view's own
        // built-in keyboard navigation is therefore redundant, and its pointerdown
        // handler calling el.focus() was stealing focus away from the search field on
        // every row click, which interrupted the PrimaryActionTriggered → toggle chain
        // and caused a spurious reload instead of a selection.
        this.cpvTreeDataView.tableView.disablesKeyboardNavigation = YES
        
        this.cpvTreeDataView.didUpdateTableAfterSearchTextChange = () => {
            this._syncTreeExpansionAfterSearchTextChange()
        }
        this.cpvTreeDataView.displayedRowsDidChangeAfterTreeExpansion = () => {
            this._scheduleFoundCompanyCountLoadingForVisibleRows()
        }
        this.cpvTreeDataView.tableView.configureWithObject({
            didScrollToPosition: EXTEND(() => this._scheduleFoundCompanyCountLoadingForVisibleRows())
        })
        
        this._updateSelectedItemLabel()
    }
    
    
    _configureTreePresentation() {
        this.cpvTreeDataView.updateRowWithNestingDepth = (_nestingDepth, rowView) => {
            rowView.viewHTMLElement.dataset.rhTreeVariant = "cpv"
        }
    }
    
    
    _setupButtonHandlers() {
        // noinspection JSConstantReassignment
        this.confirmButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._onConfirm()
        }
        // noinspection JSConstantReassignment
        this.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._selectedIds.clear()
            this._dismissWithoutSaving()
        }
        // noinspection JSConstantReassignment
        this.closeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            if (this.confirmsSelectionOnDismiss) {
                this._onConfirm()
                return
            }
            this._dismissWithoutSaving()
        }
    }
    
    
    /**
     * Attaches all keyboard handling to the search text field using the framework
     * event system. The search field is kept focused at all times while the dialog
     * is open, so all key events naturally arrive here.
     *
     * - EscDown:      clears search first; if already empty, dismisses without saving
     * - DownArrowDown / UpArrowDown: move the highlight in the data view table
     * - EnterDown:    while searching, plain Enter moves to the next direct match;
     *                 otherwise it toggles the highlighted row. Cmd/Ctrl+Enter
     *                 keeps the existing select-and-save behaviour
     * - TabDown:      toggles expand/collapse of the highlighted row (if it has children)
     */
    _setupKeyboardHandlers() {
        const searchField = this.cpvTreeDataView.searchTextField
        
        // Guard against the same physical keydown event being delivered to both
        // the inner UITextField and the outer SearchTextField wrapper, which would
        // fire each handler twice and cause the highlight to skip two rows at once.
        let _lastArrowEventTimestamp = -1
        let _lastEnterEventTimestamp = -1
        
        // noinspection JSConstantReassignment
        searchField.textElementView.controlEventTargetAccumulator.EscDown = (_sender, event) => {
            event.preventDefault()
            const currentText = searchField.text
            if (currentText?.length > 0) {
                // First Escape: clear the search field
                searchField.text = ""
                searchField.sendControlEventForKey(UITextField.controlEvent.TextChange, nil)
                this._updateHighlightedRowIndex(undefined)
            }
            else {
                // Second Escape (field already empty): dismiss without saving
                this._dismissWithoutSaving()
            }
        }
        
        // noinspection JSConstantReassignment
        searchField.textElementView.controlEventTargetAccumulator.DownArrowDown = (_sender, event) => {
            event.preventDefault()
            const ts = (event as KeyboardEvent).timeStamp
            if (ts === _lastArrowEventTimestamp) {
                return
            }
            _lastArrowEventTimestamp = ts
            const filteredData = this.cpvTreeDataView.filteredData
            if (!filteredData.length) {
                return
            }
            const current = this._highlightedRowIndex ?? -1
            const next = Math.min(current + 1, filteredData.length - 1)
            if (next !== current) {
                this._updateHighlightedRowIndex(next)
            }
        }
        
        // noinspection JSConstantReassignment
        searchField.textElementView.controlEventTargetAccumulator.UpArrowDown = (_sender, event) => {
            event.preventDefault()
            const ts = (event as KeyboardEvent).timeStamp
            if (ts === _lastArrowEventTimestamp) {
                return
            }
            _lastArrowEventTimestamp = ts
            const filteredData = this.cpvTreeDataView.filteredData
            if (!filteredData.length) {
                return
            }
            const current = this._highlightedRowIndex ?? 0
            const next = Math.max(current - 1, 0)
            if (next !== current) {
                this._updateHighlightedRowIndex(next)
            }
        }
        
        // noinspection JSConstantReassignment
        searchField.textElementView.controlEventTargetAccumulator.EnterDown = (_sender, event) => {
            event.preventDefault()
            const keyboardEvent = event as KeyboardEvent
            if (keyboardEvent.timeStamp === _lastEnterEventTimestamp) {
                return
            }
            _lastEnterEventTimestamp = keyboardEvent.timeStamp
            
            const isSaveAndClose = keyboardEvent.metaKey || keyboardEvent.ctrlKey
            const searchText = searchField.text.trim()
            if (!isSaveAndClose && searchText.length > 0) {
                this._moveHighlightToNextSearchMatch()
                return
            }
            
            // Both plain Enter and Cmd/Ctrl+Enter toggle the highlighted row if it is not yet selected
            if (this._highlightedRowIndex !== undefined) {
                const filteredData = this.cpvTreeDataView.filteredData
                const dataItem = filteredData[this._highlightedRowIndex]
                if (dataItem && !this._selectedIds.has(dataItem.id)) {
                    this._toggleSelection(dataItem.id)
                    this._refreshVisibleRows()
                    this._updateSelectedItemLabel(dataItem.text)
                }
            }
            // Only Cmd/Ctrl+Enter saves and closes, and only if something is selected
            if (isSaveAndClose && this._selectedIds.size > 0) {
                this._onConfirm()
            }
        }
        
        // noinspection JSConstantReassignment
        searchField.textElementView.controlEventTargetAccumulator.TabDown = async (_sender, event) => {
            event.preventDefault()
            if (this._highlightedRowIndex === undefined) {
                return
            }
            const filteredData = this.cpvTreeDataView.filteredData
            const dataItem = filteredData[this._highlightedRowIndex]
            if (!dataItem || !dataItem.children || dataItem.children.length === 0) {
                return
            }
            // Toggle the expansion state using the same internal key the framework uses
            const isCurrentlyOpen = dataItem._CB_DataView_ManualExpansion === "open"
            dataItem._CB_DataView_ManualExpansion = isCurrentlyOpen ? "closed" : "open"
            const scrollTop = this.cpvTreeDataView.tableView.viewHTMLElement.scrollTop
            this.cpvTreeDataView.keepRowStaticDuringNextUpdate(
                this.cpvTreeDataView.tableView.visibleRowWithIndex(this._highlightedRowIndex) as CBTableRowView<CPVNode>
            )
            await this.cpvTreeDataView.updateTableDataByFiltering()
            this.cpvTreeDataView.tableView.viewHTMLElement.scrollTop = scrollTop
            this.setNeedsLayoutUpToRootView()
            this._scheduleFoundCompanyCountLoadingForVisibleRows()
        }
    }
    
    
    /** Updates the highlighted row index and refreshes the visible row backgrounds. */
    _updateHighlightedRowIndex(newIndex: number | undefined) {
        const tableView = this.cpvTreeDataView.tableView
        this._highlightedRowIndex = newIndex
        this._highlightedNodeId = IF(newIndex !== undefined)(() =>
            this.cpvTreeDataView.filteredData[newIndex!]?.id as string | undefined
        ).ELSE(() =>
            undefined
        )
        
        // Keep UITableView's redraw state in sync without focusing its DOM element.
        // The table's own keyboard listeners remain disabled, so the search field
        // continues to receive every key event.
        if (newIndex === undefined) {
            tableView._clearKeyboardFocus()
        }
        else {
            tableView._setKeyboardFocus(newIndex, 0)
        }
        
        // Let updateCell restore the complete row presentation. This preserves
        // non-highlight backgrounds such as the summation-over-limit state.
        this._refreshVisibleRows()
    }
    
    
    /** Returns focus to the search field, keeping keyboard events flowing to it. */
    _focusSearchField() {
        this.cpvTreeDataView.searchTextField.focus()
    }
    
    
    _applyTableHeaderTypography() {
        const headerView = this.cpvTreeDataView.tableHeaderView
        
        // The heading reads as a full-width banner across the whole table rather
        // than a bar inset from both edges.
        headerView.backgroundSpansFullWidth = YES
        
        // Reapply bold after the header state update resets the only visible column.
        const titleHeaderCell = headerView.cells[0]
        if (!titleHeaderCell) {
            return
        }
        
        titleHeaderCell.titleLabel?.configureWithObject({
            style: {
                fontSize: RHDialogs.cpvTreeTableHeaderFontSize,
                fontWeight: RHDialogs.cpvTreeTableHeaderFontWeight
            }
        })
    }
    
    
    _setupDescriptors() {
        
        this.cpvTreeDataView.descriptors = [
            CBDataViewCellDescriptorWithObject<CPVNode, RHCheckbox>({
                // identifierKeyPath / parentIdentifierKeyPath live on the first descriptor
                // so the tree engine can link parent-child rows.
                // keyPath "text" is included here so the filtering array contains the
                // human-readable text — otherwise search would match against IDs only.
                keyPath: "text",
                identifierKeyPath: "id",
                parentIdentifierKeyPath: "_parent",
                title: "CPV-kood / kirjeldus / leitud ettevõtete arv",
                allowSorting: NO,
                initCustomView: () => {
                    const checkbox = new RHCheckbox()
                    checkbox.selectionAccent = RHDialogs.cpvTreeCheckboxSelectionAccent
                    return checkbox
                },
                initCell: cellView => {
                    // Leave room for the tree expansion triangle at the left edge
                    cellView.contentInsets.left = 24
                    // buttonWasPressed is not fired because RHCheckbox consumes the event,
                    // so we attach the handler directly to the checkbox here.
                    // noinspection JSConstantReassignment
                    cellView.customView.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                        if (this._isUpdatingCells) {
                            return
                        }
                        const dataItem = this._cellDataMap.get(cellView.customView)
                        if (!dataItem) {
                            return
                        }
                        if (this._selectionIsLockedForNodeId(dataItem.id)) {
                            this._ensureLockedCodesAreSelected()
                            this._focusSearchField()
                            return
                        }
                        // Track which row was interacted with so arrow keys continue from here
                        const filteredData = this.cpvTreeDataView.filteredData
                        const rowIndex = filteredData.indexOf(dataItem)
                        if (rowIndex !== -1) {
                            this._updateHighlightedRowIndex(rowIndex)
                        }
                        const wasSelected = this._selectedIds.has(dataItem.id)
                        this._toggleSelection(dataItem.id)
                        this._refreshVisibleRows()
                        this._updateSelectedItemLabel(wasSelected ? undefined : dataItem.text)
                        // Return focus to the search field after the click interaction
                        this._focusSearchField()
                    }
                },
                updateCell: (cellView, dataItem) => {
                    this._isUpdatingCells = YES
                    this._cellDataMap.set(cellView.customView, dataItem)
                    const isSelected = this._selectedIds.has(dataItem.id)
                    const isSelectionLocked = this._selectionIsLockedForNodeId(dataItem.id)
                    // Bold only when this node itself matches ALL words in the search query.
                    const searchWords = this._searchWordsForText(
                        this.cpvTreeDataView.searchTextField.text
                    )
                    const isDirectMatch = this._nodeMatchesSearchWords(dataItem, searchWords)
                    
                    // Highlight the keyboard-focused row with a subtle background tint.
                    const isHighlighted = dataItem.id === this._highlightedNodeId
                    
                    const rowView = cellView.superview as CBTableRowView<CPVNode> | undefined
                    // Cell binding reads caches only. Missing metadata is loaded after
                    // the filtered table has painted, never during row measurement.
                    const shouldLoadRowMetadata = NO
                    const summationInfo = this.summationInfoForCPVCode?.(
                        dataItem.id,
                        shouldLoadRowMetadata
                    )
                    const isOnSelectedPath = isSelected || this._ancestorIdsOfSelected.has(dataItem.id)
                    const isSummationOverLimit = isOnSelectedPath && summationInfo?.isOverLimit === YES
                    
                    let titleFontWeight = "400"
                    if (isDirectMatch) {
                        titleFontWeight = "600"
                    }
                    if (isSummationOverLimit) {
                        titleFontWeight = "700"
                    }
                    
                    let titleTextColor: UIColor = RHColor.dark
                    if (isSummationOverLimit) {
                        titleTextColor = RHColors.dangerBadgeText
                    }
                    
                    cellView.customView.configureWithObject({
                        enabled: !isSelectionLocked,
                        titleLabel: {
                            text: this._displayTextForNode(dataItem, shouldLoadRowMetadata, summationInfo),
                            textColor: titleTextColor,
                            style: {
                                fontWeight: titleFontWeight
                            }
                        }
                    })
                    if (rowView) {
                        let rowBackgroundColor = UIColor.clearColor
                        if (isSummationOverLimit) {
                            rowBackgroundColor = RHColors.pinkGlass
                        }
                        if (isHighlighted) {
                            rowBackgroundColor = new UIColor("rgba(0, 100, 255, 0.07)")
                        }
                        if (isHighlighted && isSummationOverLimit) {
                            rowBackgroundColor = RHColors.softDangerFillAlpha20
                        }
                        rowView.backgroundView.backgroundColor = rowBackgroundColor
                    }
                    cellView.customView.selected = isSelected
                    // O(1) lookup into the precomputed ancestor set instead of a
                    // recursive tree walk on every rendered row.
                    if (this.showsAncestorSelectionState) {
                        cellView.customView.indeterminate = !isSelected && this._ancestorIdsOfSelected.has(dataItem.id)
                    }
                    else {
                        cellView.customView.indeterminate = NO
                    }
                    this._isUpdatingCells = NO
                }
            })
        ]
        
        this._applyTableHeaderTypography()
        
    }
    
    
    async show(
        initialSelectedCodes: string[] = [],
        onConfirmed?: (selectedCodes: string[]) => void,
        onClose?: () => void
    ) {
        this._onSelectionConfirmed = onConfirmed
        this._onClose = onClose
        this._applyConfiguredDialogText()
        this._selectedIds = new Set(initialSelectedCodes)
        this._ensureLockedCodesAreSelected()
        this._saveIsSuppressedOnDismiss = NO
        this._rebuildAncestorIds()
        
        if (!this._isDataLoaded) {
            await this._loadCPVData()
        }
        else {
            // Re-setup descriptors so updateCell closures see the fresh _selectedIds set
            this._setupDescriptors()
            await this.cpvTreeDataView.updateTableDataByFiltering()
        }
        
        this._rebuildAncestorIds()
        this._updateSelectedItemLabel()
        
        // Expand the tree to the selected nodes (if any) after data/descriptors are ready
        if (this._selectedIds.size > 0) {
            await this._expandToSelectedNodes()
        }
        
        this._initHighlight()
        this.showInRootView(YES)
        this._scheduleMetadataLoadingForVisibleRows()
        
        if (!this._appearedAnimated) {
            this._focusSearchField()
        }
        
    }
    
    
    /**
     * Sets _highlightedRowIndex to the position of the currently selected node
     * in filteredData, or falls back to 0 if nothing is selected or the selected
     * node is not visible (e.g. still collapsed).
     */
    _initHighlight() {
        const filteredData = this.cpvTreeDataView.filteredData
        if (!filteredData.length) {
            this._updateHighlightedRowIndex(undefined)
            return
        }
        if (this._selectedIds.size > 0) {
            const selectedId = Array.from(this._selectedIds)[0]
            const index = filteredData.findIndex(item => item.id === selectedId)
            if (index !== -1) {
                this._updateHighlightedRowIndex(index)
                return
            }
        }
        this._updateHighlightedRowIndex(0)
    }
    
    
    /**
     * Walks up the ancestor chains of all selected nodes, marking each ancestor
     * as manually expanded so the tree engine will reveal every selected row.
     * After updating the filtered data, scrolls the first selected row into view.
     */
    async _expandToSelectedNodes() {
        const selectedIds = Array.from(this._selectedIds)
        if (selectedIds.length === 0) {
            return
        }
        
        const flatData = this.cpvTreeDataView.data
        const dataByIdentifier = new Map(flatData.map(item => [item.id, item] as const))
        
        for (const selectedId of selectedIds) {
            // Walk up the ancestor chain and mark each ancestor as manually open
            const selectedNode = dataByIdentifier.get(selectedId)
            if (!selectedNode) {
                continue
            }
            
            let currentNode: CPVNode | undefined = selectedNode
            while (currentNode) {
                const parentId = currentNode._parent
                if (!parentId) {
                    break
                }
                const parent = dataByIdentifier.get(parentId)
                if (!parent) {
                    break
                }
                rowStateOf(parent)._CB_DataView_ManualExpansion = "open"
                currentNode = parent
            }
        }
        
        // Re-filter so the tree reflects the newly expanded ancestors
        await this.cpvTreeDataView.updateTableDataByFiltering()
        
        // Scroll the selected row element into view after a short tick so the
        // DOM has had a chance to render the newly visible rows.
        setTimeout(() => {
            this._scrollToSelectedRow(selectedIds[0])
        }, 80)
    }
    
    
    /**
     * After the filtered data updates, immediately highlights the first row whose
     * own text matches every search word. Ancestor-only context rows are skipped.
     * Resets the highlight when search is empty.
     */
    _syncTreeExpansionAfterSearchTextChange() {
        const searchText = this.cpvTreeDataView.searchTextField.text.trim()
        const shouldResetExpansion = searchText.length === 0 && this._lastSearchTextForExpansionReset.length > 0
        this._lastSearchTextForExpansionReset = searchText
        this._scheduleMetadataLoadingForVisibleRows()
        
        if (!shouldResetExpansion) {
            this._updateHighlightForSearchText()
            return
        }
        
        this._collapseTreeToDefaultExpansion()
        this.cpvTreeDataView.updateTableDataByFiltering().then(() => {
            this.cpvTreeDataView.tableView.viewHTMLElement.scrollTop = 0
            this._updateHighlightForSearchText()
            this.setNeedsLayoutUpToRootView()
        })
    }
    
    
    _collapseTreeToDefaultExpansion() {
        const flatData = this.cpvTreeDataView.data
        for (const dataItem of flatData) {
            const rowState = rowStateOf(dataItem)
            rowState._CB_DataView_ManualExpansion = undefined
            rowState._CB_DataView_IsExpanded = NO
        }
    }
    
    
    _updateHighlightForSearchText() {
        const searchText = this.cpvTreeDataView.searchTextField.text.trim().toLowerCase()
        
        if (!searchText) {
            this._updateHighlightedRowIndex(undefined)
            return
        }
        
        const matchingRowIndexes = this._rowIndexesMatchingCurrentSearchText()
        if (!matchingRowIndexes.length) {
            this._updateHighlightedRowIndex(undefined)
            return
        }
        
        this._updateHighlightedRowIndex(matchingRowIndexes[0])
    }
    
    
    _searchWordsForText(searchText: string) {
        if (searchText === this._cachedSearchTextForWords) {
            return this._cachedSearchWords
        }
        this._cachedSearchTextForWords = searchText
        this._cachedSearchWords = searchText
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(searchWord => searchWord.length > 0)
        return this._cachedSearchWords
    }
    
    
    _nodeMatchesSearchWords(node: CPVNode, searchWords: string[]) {
        if (!searchWords.length) {
            return NO
        }
        const searchableNodeText = `${node.id} ${node.text}`.toLowerCase()
        return searchWords.every(searchWord => searchableNodeText.includes(searchWord))
    }
    
    
    _rowIndexesMatchingCurrentSearchText() {
        const searchWords = this._searchWordsForText(
            this.cpvTreeDataView.searchTextField.text
        )
        if (!searchWords.length) {
            return []
        }
        
        const filteredData = this.cpvTreeDataView.filteredData
        const matchingRowIndexes: number[] = []
        for (let rowIndex = 0; rowIndex < filteredData.length; rowIndex++) {
            if (this._nodeMatchesSearchWords(filteredData[rowIndex], searchWords)) {
                matchingRowIndexes.push(rowIndex)
            }
        }
        return matchingRowIndexes
    }
    
    
    _moveHighlightToNextSearchMatch() {
        const matchingRowIndexes = this._rowIndexesMatchingCurrentSearchText()
        if (!matchingRowIndexes.length) {
            return
        }
        
        let nextRowIndex = matchingRowIndexes[0]
        const highlightedRowIndex = this._highlightedRowIndex
        if (highlightedRowIndex !== undefined) {
            const rowIndexAfterHighlight = matchingRowIndexes.find(
                rowIndex => rowIndex > highlightedRowIndex
            )
            if (rowIndexAfterHighlight !== undefined) {
                nextRowIndex = rowIndexAfterHighlight
            }
        }
        this._updateHighlightedRowIndex(nextRowIndex)
    }
    
    _scrollToSelectedRow(selectedId: string) {
        // Walk every rendered row in the table view and find the one whose
        // data item matches the selected ID.
        const tableEl = this.cpvTreeDataView.tableView.viewHTMLElement
        if (!tableEl) {
            return
        }
        
        const rows = tableEl.querySelectorAll<HTMLElement>("[data-row-index]")
        const filteredData = this.cpvTreeDataView.filteredData
        
        for (const rowEl of Array.from(rows)) {
            const indexStr = rowEl.getAttribute("data-row-index")
            if (indexStr == null) {
                continue
            }
            const index = parseInt(indexStr, 10)
            const dataItem = filteredData[index]
            if (dataItem?.id === selectedId) {
                // Native scrollIntoView() walks every scrollable ancestor —
                // including the page itself — and will scroll the whole
                // document if it judges the row "out of view" relative to
                // the row's transform-positioned container. That is what
                // produced the page-jumps-to-top symptom. Centering the row
                // within tableEl's own scrollTop keeps the scroll confined
                // to the table, matching the table-local keyboard highlighting.
                const rowOffsetTop = rowEl.offsetTop
                const centeredScrollTop = rowOffsetTop - (tableEl.clientHeight - rowEl.offsetHeight) / 2
                tableEl.scrollTop = Math.max(0, centeredScrollTop)
                return
            }
        }
        
        // Fallback: if no data-row-index attribute, just scroll the tableView
        // container to the approximate position of the item in the filtered list
        const selectedIndex = filteredData.findIndex(item => item.id === selectedId)
        if (selectedIndex >= 0) {
            const rowHeight = this.cpvTreeDataView.tableView.viewHTMLElement.scrollHeight /
                Math.max(filteredData.length, 1)
            this.cpvTreeDataView.tableView.viewHTMLElement.scrollTop = selectedIndex * rowHeight - 100
        }
    }
    
    
    override animationDidFinish() {
        super.animationDidFinish()
        
        if (this.isVisible && this.isMemberOfViewTree) {
            this._focusSearchField()
        }
    }
    
    
    override dismiss() {
        // If dismiss is called without _saveIsSuppressedOnDismiss being set (i.e. tap-outside),
        // save the current selection only when this picker explicitly opts into
        // the legacy close/backdrop-as-confirm behavior.
        if (!this._saveIsSuppressedOnDismiss && this.confirmsSelectionOnDismiss) {
            this._onSelectionConfirmed?.(Array.from(this._selectedIds))
        }
        this._onClose?.()
        super.dismiss()
    }
    
    
    async _loadCPVData() {
        
        try {
            // @ts-ignore — RetrieveCPVTree is declared in SocketClientFunctions_PATCH
            const result = (await SocketClient.RetrieveCPVTree({})).result as CPVNode[]
            
            if (result && result.length) {
                this._cpvTreeData = result
                this._isDataLoaded = true
                // Descriptors must be set before setTreeData so identifierKeyPath is known
                this._setupDescriptors()
                this.cpvTreeDataView.setTreeData(this._cpvTreeData, "children", NO)
            }
        }
        catch (error) {
            console.error("Error loading CPV tree:", error)
            CBDialogViewShower.alert("CPV-koodide laadimine ebaõnnestus")
        }
        
    }
    
    
    /**
     * Rebuilds _ancestorIdsOfSelected by walking the flat data upward from each
     * selected node via the _parent links. Runs once per selection change — O(depth)
     * per selected node, O(1) per row render.
     *
     * Must be called after any mutation to _selectedIds or after tree data is loaded.
     */
    _rebuildAncestorIds() {
        this._ancestorIdsOfSelected = new Set<string>()
        
        const flatData = this.cpvTreeDataView.data
        if (!flatData.length) {
            return
        }
        
        const dataById = new Map(flatData.map(item => [item.id, item] as const))
        
        for (const selectedId of this._selectedIds) {
            let node = dataById.get(selectedId)
            while (node) {
                const parentId = node._parent
                if (!parentId) {
                    break
                }
                if (this._ancestorIdsOfSelected.has(parentId)) {
                    // Already walked this branch (can happen with multiple selections)
                    break
                }
                this._ancestorIdsOfSelected.add(parentId)
                node = dataById.get(parentId)
            }
        }
    }
    
    
    _applyConfiguredDialogText() {
        this.titleLabel.text = this.dialogTitle
        this.confirmButton.configureWithObject({ titleLabel: { text: this.confirmButtonTitle } })
        this.cancelButton.configureWithObject({ titleLabel: { text: this.cancelButtonTitle } })
    }
    
    
    _toggleSelection(nodeId: string) {
        if (this._selectionIsLockedForNodeId(nodeId)) {
            this._ensureLockedCodesAreSelected()
            this._rebuildAncestorIds()
            return
        }
        
        if (this.selectionExpansionMode === "selectDescendantsOnAdd" && this.allowsMultipleSelection) {
            this._toggleSelectionWithDescendantExpansion(nodeId)
            this.selectionDidChange?.(Array.from(this._selectedIds))
            return
        }
        
        if (this._selectedIds.has(nodeId)) {
            // Single-select clears everything; multi-select removes just this node.
            if (this.allowsMultipleSelection) {
                this._selectedIds.delete(nodeId)
            }
            else {
                this._selectedIds.clear()
            }
        }
        else {
            // Single-select keeps exactly one code; multi-select accumulates.
            if (!this.allowsMultipleSelection) {
                this._selectedIds.clear()
            }
            this._selectedIds.add(nodeId)
        }
        this._ensureLockedCodesAreSelected()
        this._rebuildAncestorIds()
        this.selectionDidChange?.(Array.from(this._selectedIds))
    }
    
    
    _toggleSelectionWithDescendantExpansion(nodeId: string) {
        const nodeAndDescendantIds = this._nodeAndDescendantIdsForNodeId(nodeId)
        if (this._selectedIds.has(nodeId)) {
            for (const descendantId of nodeAndDescendantIds) {
                this._selectedIds.delete(descendantId)
            }
        }
        else {
            for (const descendantId of nodeAndDescendantIds) {
                this._selectedIds.add(descendantId)
            }
        }
        this._ensureLockedCodesAreSelected()
        this._rebuildAncestorIds()
    }
    
    
    _selectionIsLockedForNodeId(nodeId: string) {
        return this.lockedSelectedCodes.includes(nodeId)
    }
    
    
    _ensureLockedCodesAreSelected() {
        for (const lockedSelectedCode of this.lockedSelectedCodes) {
            this._selectedIds.add(lockedSelectedCode)
        }
    }
    
    
    _nodeAndDescendantIdsForNodeId(nodeId: string): string[] {
        const flatData = (this.cpvTreeDataView.data) ?? []
        const node = flatData.find(item => item.id === nodeId)
        if (!node) {
            return [nodeId]
        }
        
        const result: string[] = []
        this._appendNodeAndDescendantIdsToArray(node, result)
        return result
    }
    
    
    _appendNodeAndDescendantIdsToArray(node: CPVNode, result: string[]) {
        result.push(node.id)
        for (const child of node.children ?? []) {
            this._appendNodeAndDescendantIdsToArray(child, result)
        }
    }
    
    
    _selectedNodeText(nodeId: string) {
        const flatData = (this.cpvTreeDataView.data) ?? []
        return flatData.find(item => item.id === nodeId)?.text ?? nodeId
    }
    
    
    _displayTextForNode(
        node: CPVNode,
        shouldLoadRowMetadata = YES,
        summationInfo?: CPVSummationRowInfo
    ): string {
        if (
            !this.foundCompanyCountForCPVCode &&
            !this.inviteCountForCPVCode &&
            !this.summationInfoForCPVCode
        ) {
            return node.text
        }
        
        const suffixes: string[] = []

        if (this.foundCompanyCountForCPVCode) {
            const foundCompanyCount = this.foundCompanyCountForCPVCode(
                node.id,
                shouldLoadRowMetadata
            )
            if (foundCompanyCount !== undefined) {
                suffixes.push(`Leitud ettevõtteid: ${foundCompanyCount}`)
            }
        }
        
        if (this.inviteCountForCPVCode) {
            const count = this.inviteCountForCPVCode(node.id, shouldLoadRowMetadata)
            if (count !== undefined) {
                suffixes.push(`${count} pakkujat`)
            }
        }
        
        if (summationInfo !== undefined) {
            suffixes.push(`Σ ${RHEuroFormatter.formattedAmount(summationInfo.amount)}`)
        }
        
        if (suffixes.length === 0) {
            return node.text
        }
        
        return `${node.text} · ${suffixes.join(" · ")}`
    }
    
    
    refreshDisplayedRows() {
        if (this._hasScheduledDisplayedRowsRefresh) {
            return
        }
        this._hasScheduledDisplayedRowsRefresh = YES
        requestAnimationFrame(() => {
            this._hasScheduledDisplayedRowsRefresh = NO
            if (!this.cpvTreeDataView.tableView.isMemberOfViewTree) {
                return
            }
            this._refreshVisibleRows()
            this._scheduleMetadataLoadingForVisibleRows()
        })
    }
    
    
    _refreshVisibleRows() {
        const tableView = this.cpvTreeDataView.tableView
        for (const rowView of tableView._visibleRows as CBTableRowView<CPVNode>[]) {
            const dataItem = rowView.data
            if (!dataItem) {
                continue
            }
            rowView.data = dataItem
            rowView.setNeedsLayout()
        }
        tableView.setNeedsLayout()
    }
    
    
    _scheduleMetadataLoadingForVisibleRows() {
        if (
            !this.foundCompanyCountForCPVCode &&
            !this.inviteCountForCPVCode &&
            !this.summationInfoForCPVCode
        ) {
            return
        }
        if (this._hasScheduledMetadataLoadForVisibleRows) {
            return
        }
        this._hasScheduledMetadataLoadForVisibleRows = YES
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this._hasScheduledMetadataLoadForVisibleRows = NO
                this._loadMetadataForVisibleRows()
            })
        })
    }
    
    
    _loadMetadataForVisibleRows() {
        if (
            !this.foundCompanyCountForCPVCode &&
            !this.inviteCountForCPVCode &&
            !this.summationInfoForCPVCode
        ) {
            return
        }
        if (!this.cpvTreeDataView.tableView.isMemberOfViewTree) {
            return
        }
        
        const tableView = this.cpvTreeDataView.tableView
        for (const rowView of tableView._visibleRows as CBTableRowView<CPVNode>[]) {
            const dataItem = rowView.data
            if (!dataItem?.id) {
                continue
            }
            this.foundCompanyCountForCPVCode?.(dataItem.id, YES)
            this.inviteCountForCPVCode?.(dataItem.id, YES)
            this.summationInfoForCPVCode?.(dataItem.id, YES)
        }
    }


    _scheduleFoundCompanyCountLoadingForVisibleRows() {
        if (!this.foundCompanyCountForCPVCode || this._hasScheduledFoundCompanyCountLoadForVisibleRows) {
            return
        }
        this._hasScheduledFoundCompanyCountLoadForVisibleRows = YES
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this._hasScheduledFoundCompanyCountLoadForVisibleRows = NO
                if (!this.cpvTreeDataView.tableView.isMemberOfViewTree) {
                    return
                }

                for (const rowView of this.cpvTreeDataView.tableView._visibleRows as CBTableRowView<CPVNode>[]) {
                    const cpvCode = rowView.data?.id
                    if (cpvCode) {
                        this.foundCompanyCountForCPVCode?.(cpvCode, YES)
                    }
                }
            })
        })
    }
    
    
    _applySelectedItemBannerState(isSelected: boolean) {
        const backgroundColor = isSelected
                                ? RHDialogs.cpvTreeSelectedSummarySelectedBackgroundColor
                                : RHDialogs.cpvTreeSelectedSummaryUnselectedBackgroundColor
        const borderColor = isSelected
                            ? RHDialogs.cpvTreeSelectedSummarySelectedBorderColor
                            : RHDialogs.cpvTreeSelectedSummaryUnselectedBorderColor
        const textColor = isSelected
                          ? RHDialogs.cpvTreeSelectedSummarySelectedTextColor
                          : RHDialogs.cpvTreeSelectedSummaryUnselectedTextColor
        const boxShadow = isSelected
                          ? RHDialogs.cpvTreeSelectedSummarySelectedShadow
                          : RHDialogs.cpvTreeSelectedSummaryUnselectedShadow
        
        this.selectedItemBannerView.backgroundColor = backgroundColor
        this.selectedItemBannerView.style.border = `1px solid ${borderColor.stringValue}`
        this.selectedItemBannerView.style.boxShadow = boxShadow
        this.selectedItemLabel.textColor = textColor
        this.selectedItemLabel.style.fontWeight = isSelected ? "600" : "500"
    }
    
    
    _updateSelectedItemLabel(selectedText?: string) {
        const selectedIds = Array.from(this._selectedIds)
        
        if (selectedIds.length === 0) {
            this.selectedItemLabel.text = "Pole valitud"
            this._applySelectedItemBannerState(false)
            return
        }
        
        if (selectedIds.length === 1) {
            const onlyText = selectedText ?? this._selectedNodeText(selectedIds[0])
            this.selectedItemLabel.text = `Valitud: ${onlyText}`
        }
        else {
            const texts = selectedIds.map(nodeId => this._selectedNodeText(nodeId))
            this.selectedItemLabel.text = `Valitud (${selectedIds.length}): ${texts.join(", ")}`
        }
        
        this._applySelectedItemBannerState(true)
    }
    
    
    _footerButtonWidth(button: RHButton) {
        return Math.min(
            RHDialogs.cpvTreeFooterButtonMaxWidth,
            Math.max(
                RHDialogs.cpvTreeFooterButtonMinWidth,
                button.titleLabel.intrinsicContentWidth(RHDialogs.cpvTreeFooterButtonHeight) +
                RHDialogs.cpvTreeFooterButtonContentPadding * 2 +
                RHDialogs.cpvTreeFooterButtonTextSafetyBuffer
            )
        )
    }


    _dialogWidthForAvailableWidth(availableWidth: number): number {
        return Math.min(
            RHDialogs.cpvTreeDialogMaxWidth,
            availableWidth * RHDialogs.cpvTreeDialogWidthViewportRatio
        )
    }


    _treeViewHeightForAvailableHeight(availableHeight: number): number {
        return Math.min(
            RHDialogs.cpvTreeTreeViewHeight,
            Math.max(RHDialogs.cpvTreeMinimumTreeViewHeight, availableHeight)
        )
    }
    
    
    _onConfirm() {
        // Mark that dismiss() should NOT fire the callback a second time
        this._saveIsSuppressedOnDismiss = YES
        this._onSelectionConfirmed?.(Array.from(this._selectedIds))
        super.dismiss()
        this._onClose?.()
    }
    
    
    /** Dismiss without saving — used by the Cancel button and Escape. */
    _dismissWithoutSaving() {
        this._saveIsSuppressedOnDismiss = YES
        super.dismiss()
        this._onClose?.()
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const dialogBounds = this.contentBounds
        const closeButtonSize = RHButtons.dialogCloseSize
        
        const dialogWidth = this._dialogWidthForAvailableWidth(dialogBounds.width)
        
        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)
        
        const bounds = this.contentView.contentBoundsWithInset(padding * 1.5)
        const headerRowFrame = bounds.rectangleWithHeight(labelHeight * 1.5)
        const titleLabelFrame = headerRowFrame.rectangleWithWidth(
            headerRowFrame.width - closeButtonSize - padding
        )
        
        titleLabelFrame
            .assignedAsFrameOfView(this.titleLabel)
        
        titleLabelFrame
            .rectangleForNextColumn(padding, closeButtonSize)
            .rectangleWithHeight(closeButtonSize, 0.5)
            .assignedAsFrameOfView(this.closeButton)
        
        let cancelButtonWidth = this._footerButtonWidth(this.cancelButton)
        let confirmButtonWidth = this._footerButtonWidth(this.confirmButton)
        let footerButtonsRowWidth = cancelButtonWidth + confirmButtonWidth + RHDialogs.cpvTreeFooterButtonGap
        
        if (footerButtonsRowWidth > bounds.width) {
            const compactButtonWidth = Math.max(
                0,
                (bounds.width - RHDialogs.cpvTreeFooterButtonGap) * 0.5
            )
            cancelButtonWidth = compactButtonWidth
            confirmButtonWidth = compactButtonWidth
            footerButtonsRowWidth = bounds.width
        }
        
        const instructionLabelFrame = headerRowFrame
            .rectangleForNextRow(padding * 0.5, labelHeight * 0.85)
            .assignedAsFrameOfView(this.instructionLabel)

        const heightReservedBelowTree =
            padding * 0.5 + RHDialogs.cpvTreeSelectedSummaryHeight +
            padding * 0.5 + RHDialogs.cpvTreeFooterButtonHeight +
            RHDialogs.cpvTreeFooterBottomPadding
        const maximumDialogHeight =
            dialogBounds.height * RHDialogs.cpvTreeDialogHeightViewportRatio
        const availableTreeViewHeight =
            maximumDialogHeight - instructionLabelFrame.max.y - padding -
            heightReservedBelowTree
        const treeViewHeight = this._treeViewHeightForAvailableHeight(availableTreeViewHeight)

        const footerButtonsRowFrame = instructionLabelFrame
            .rectangleForNextRow(padding, treeViewHeight)
            .assignedAsFrameOfView(this.cpvTreeDataView)
            .rectangleForNextRow(padding * 0.5, RHDialogs.cpvTreeSelectedSummaryHeight)
            .assignedAsFrameOfView(this.selectedItemBannerView)
            .rectangleForNextRow(padding * 0.5, RHDialogs.cpvTreeFooterButtonHeight)
        
        this.selectedItemBannerView.contentBoundsWithInsets(
            RHDialogs.cpvTreeSelectedSummaryHorizontalInset,
            RHDialogs.cpvTreeSelectedSummaryHorizontalInset,
            0,
            0
        )
            .assignedAsFrameOfView(this.selectedItemLabel)
        this.selectedItemLabel.style.lineHeight = `${this.selectedItemLabel.frame.height}px`
        
        footerButtonsRowFrame
            .rectangleWithWidth(footerButtonsRowWidth, 0.5)
            .distributeViewsAlongWidth(
                [this.cancelButton, this.confirmButton],
                undefined,
                RHDialogs.cpvTreeFooterButtonGap,
                [cancelButtonWidth, confirmButtonWidth]
            )
        
        const finalHeight = this.confirmButton.frame.max.y + RHDialogs.cpvTreeFooterBottomPadding
        
        // Center when the dialog fits the viewport; when its natural content is
        // taller than that, rectangleWithHeight(finalHeight, 0.5) would push the
        // top edge above y = 0 (unreachable — the backdrop can't scroll to a
        // negative offset) with no padding at either edge. Clamp the top instead
        // and let the backdrop's own scroll reach the rest, as intended.
        let dialogFrame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)
        
        if (dialogFrame.y < padding) {
            dialogFrame = dialogFrame.rectangleWithY(padding)
        }
        
        this.contentView.frame = dialogFrame
        
        // UIView positions children via CSS transform (translate3d), not via
        // top/left, so a child's footprint inside an overflow:auto parent comes
        // only from its own CSS height — never from where its transform visually
        // places it. To make the backdrop's scrollable area actually reach
        // `padding` past contentView's bottom edge, the spacer's height itself
        // (not its y) must extend that far.
        this.bottomSpacerView.frame = dialogBounds.rectangleWithHeight(dialogFrame.max.y + padding)
    }
    
}
